import React, { useEffect } from 'react';
import { SelectOptionWithGroup } from 'tea-component';
import { getCalendarDataFetchParams, getMilestoneOptions } from '../utils';
import { KEYS_MAPPING } from '@gp/constants/field';
import {
  DBG_DETAILS_CUR_MILESTONE_ID,
} from '@gp/constants/const';
import type { IShortUrlParams } from '@gp/types/global';
import type { ICalendarTableDTO } from '@gp/types/calendar';
import type { ScheduleListType } from '@gp/types/overview';
import type { IDateValue } from '@gp/components/ComposedDatePicker/types';
import type { IScheduleFilterMetadata } from './useOptionsAndValues';

// 兜底选择自动尝试获取数据的segment，当自动尝试获取数据都没有数据时，设置为这个segment为选中的segment
// const BACK_SEGMENT: ScheduleSegmentTypes = '1';
const BACK_SEGMENT: ScheduleSegmentTypes = '2';
// 自动尝试获取数据的segment顺序
const AUTO_TRY_SEGMENTS: ScheduleSegmentTypes[] = [BACK_SEGMENT, '2'];

const getIsCalendarCardEmpty = (list: ScheduleListType): boolean => {
  if (!Array.isArray(list) || !list.length) return true;
  return list.every((item) => {
    const { list: subList } = item || {};
    return !Array.isArray(subList) || !subList.length;
  });
};

const getIsCalendarTableEmpty = (data: {
  start: string;
  end: string;
  list: ICalendarTableDTO[];
}): boolean => {
  const { list } = data || {};
  return !Array.isArray(list) || !list.length;
};

export default function useInit(props: {
  tab: ScheduleTabTypes;
  setTab: React.Dispatch<React.SetStateAction<ScheduleTabTypes>>;
  segment: ScheduleSegmentTypes;
  setShowGranularitySelect: React.Dispatch<React.SetStateAction<boolean>>;
  defaultDate: { month: IDateValue; quarter: IDateValue; year: IDateValue; } | undefined;
  options: IScheduleFilterMetadata<SelectOptionWithGroup>;
  values: IScheduleFilterMetadata<string>;
  filterLoading: boolean;
  filter: IPipelineOverviewScheduleFilterVO;
  setFilter: React.Dispatch<React.SetStateAction<IPipelineOverviewScheduleFilterVO>>;

  updateCalendarData: (
    tab: ScheduleTabTypes,
    segment: ScheduleSegmentTypes,
    filter: IPipelineOverviewScheduleFilterVO,
    milestoneOptions: SelectOptionWithGroup[],
  ) => void;
  getScheduleDataRunAsync: (
    params?: IPipelineOverviewScheduleFetchParams | null,
  ) => Promise<ScheduleListType>;
  getScheduleMonthlyDataRunAsync: (
    params?: IPipelineOverviewScheduleFetchParams | null,
  ) => Promise<{ start: string; end: string; list: ICalendarTableDTO[]; }>;
  onUpdateShortUrlParams: (params: IShortUrlParams) => Promise<void>;
}) {
  const {
    tab,
    setTab,
    segment,
    setShowGranularitySelect,
    defaultDate,
    options,
    values,
    filterLoading,
    filter,
    setFilter,
    updateCalendarData,
    getScheduleDataRunAsync,
    getScheduleMonthlyDataRunAsync,
    onUpdateShortUrlParams,
  } = props;

  const updateCalendarDataIntelligence = async (
    tab: ScheduleTabTypes,
    filter: IPipelineOverviewScheduleFilterVO,
    options: IScheduleFilterMetadata<SelectOptionWithGroup>,
  ) => {
    let isMatched = false;
    try {
      const fetchAction =
        tab === 'calendar-card' ? getScheduleDataRunAsync : getScheduleMonthlyDataRunAsync;
      const getIsEmpty = tab === 'calendar-card' ? getIsCalendarCardEmpty : getIsCalendarTableEmpty;
      for (const segmentItem of AUTO_TRY_SEGMENTS) {
        const milestoneOptions = getMilestoneOptions(segmentItem);
        const milestoneValues = milestoneOptions?.map((item) => item?.value) || [];
        const filterTmp = { ...filter, [KEYS_MAPPING.MILESTONE]: milestoneValues };
        const params = getCalendarDataFetchParams(
          segmentItem,
          filterTmp,
          options,
          milestoneOptions,
        );
        const res: any = await fetchAction({
          ...params,
          type: Number(segmentItem) as 1 | 2 | 3,
        });
        localStorage.setItem(DBG_DETAILS_CUR_MILESTONE_ID, '');
        if (!getIsEmpty(res)) {
          isMatched = true;
          setFilter(filterTmp);
          onUpdateShortUrlParams?.({ tab, segment: segmentItem, filter: filterTmp });
          break;
        }
      }
    } catch (exception) {
    } finally {
      if (!isMatched) {
        const backSegmentMilestoneOptions = getMilestoneOptions(BACK_SEGMENT);
        const backSegmentMilestoneValues =
          backSegmentMilestoneOptions?.map((item) => item?.value) || [];
        const backSegmentFilter = {
          ...filter,
          [KEYS_MAPPING.MILESTONE]: backSegmentMilestoneValues,
        };
        setFilter(backSegmentFilter);
        onUpdateShortUrlParams?.({ tab, segment: BACK_SEGMENT, filter: backSegmentFilter });
      }
    }
  };

  useEffect(() => {
    if (filterLoading) return;
    if (segment) {
      const milestoneOptionsTmp = getMilestoneOptions(segment) || [];
      setTab(tab);
      setShowGranularitySelect(tab === 'calendar-card');
      setFilter(filter);
      updateCalendarData(tab, segment, filter, milestoneOptionsTmp);
      onUpdateShortUrlParams?.({ tab, segment, filter });
    } else {
      const { composedDatePicker } = filter || {};
      const { granularity: initialGranularity } = composedDatePicker || {};
      const initialDateValue = defaultDate?.[initialGranularity] || ['', ''];
      const filterTmp = {
        ...filter,
        ...values,
        composedDatePicker: { ...composedDatePicker, date: initialDateValue },
      };
      setTab(tab);
      setShowGranularitySelect(tab === 'calendar-card');
      setFilter(filterTmp);
      updateCalendarDataIntelligence(tab, filterTmp, options);
    }
  }, [filterLoading, values, options, defaultDate]);
}
